import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Renderer2,
  signal,
} from '@angular/core';
import {
  BoelsCategoryHierarchy,
  BoelsCmsLinkComponent,
  Cart,
  CmsHeaderComponent,
  CmsNavigationNode,
  CmsSiteLogoComponent,
  ImageFetchPriority,
  ImageLoadingStrategy,
  UserEnum,
} from '@boels/models';
import {
  combineLatestWith,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import {
  BreadcrumbsService,
  CartFacade,
  CatalogsFacade,
  CmsFacade,
  ComponentDataService,
  CultureFacade,
  RouterStateFacade,
  SiteContextFacade,
  SlotDecoratorDirective,
  UserDataFacade,
} from '@boels/shared-state';
import { GenericLinkComponent } from '../generic-link/generic-link.component';
import { ToggleLanguageSelectorComponent } from '../toggle-language-selector/toggle-language-selector.component';
import { ToggleUserTypeComponent } from '../toggle-user-type/toggle-user-type.component';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { faBars, faShoppingBasket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ContactUsComponent } from '../contact-us/contact-us.component';
import {
  BreakpointService,
  ModalService,
  UrlSafePipe,
  WindowRef,
} from '@boels/helpers';
import { NavigationEnd, Router } from '@angular/router';
import { NavigationComponent } from '../navigation/navigation.component';
import { CustomSearchBoxComponent } from '../custom-searchbox/custom-searchbox.component';
import { NavigationMobileComponent } from '../navigation-mobile/navigation-mobile.component';
import { AccountComponent } from '../account/account.component';
import { NgClass } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LanguageSelectorModalComponent } from '../language-selector-modal/language-selector-modal.component';
import { MediaComponent } from '../media/media.component';

@Component({
  imports: [
    MediaComponent,
    GenericLinkComponent,
    ToggleUserTypeComponent,
    ToggleLanguageSelectorComponent,
    FontAwesomeModule,
    ContactUsComponent,
    BreadcrumbComponent,
    TranslocoModule,
    NavigationComponent,
    NavigationMobileComponent,
    CustomSearchBoxComponent,
    AccountComponent,
    NgClass,
    SlotDecoratorDirective,
    VerticalAlignerComponent,
  ],
  selector: 'boels-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(50%)' }),
        animate('100ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-out', style({ transform: 'translateX(50%)' })),
      ]),
    ]),
  ],
  providers: [
    RouterStateFacade,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['header'],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  #cmsFacade = inject(CmsFacade);
  #siteContextFacade = inject(SiteContextFacade);
  #catalogsFacade = inject(CatalogsFacade);
  #urlSafePipe = inject(UrlSafePipe);
  #componentDataService = inject(ComponentDataService);
  #breakpointService = inject(BreakpointService);
  #cartFacade = inject(CartFacade);
  #breadcrumbsService = inject(BreadcrumbsService);
  #renderer = inject(Renderer2);
  #windowRef = inject(WindowRef);
  #router = inject(Router);
  #cultureFacade = inject(CultureFacade);
  #userDataFacade = inject(UserDataFacade);
  #routerState = inject(RouterStateFacade);
  #modalService = inject(ModalService);
  #destroyRef = inject(DestroyRef);

  //#region properties
  protected readonly faShoppingBasket = faShoppingBasket;

  protected readonly isCountrySelectionSite = toSignal(
    this.#cmsFacade.getActivePage().pipe(
      map((baseSite) => baseSite?.uid === 'countryselection'),
      distinctUntilChanged(),
    ),
  );

  protected readonly isHomeSite = toSignal(
    this.#routerState.getUrl().pipe(
      map((url) => url?.replace(/[^/]/g, '').length === 1),
      distinctUntilChanged(),
    ),
  );

  protected readonly useWhiteHeader = signal(false);

  protected readonly loadingStrategy = ImageLoadingStrategy.EAGER;
  protected readonly fetchPriority = ImageFetchPriority.HIGH;
  protected readonly faBars = faBars;

  protected readonly mdAndBelow = toSignal(this.#breakpointService.mdAndBelow$);
  protected readonly isMobileModuleActive = signal(false);

  protected readonly siteContextSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly logoSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly miniCartSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly siteLinksSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly searchBoxSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly loginSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly navigationBarSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly placeholderSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );
  protected readonly bottomHeaderSlot = signal<CmsHeaderComponent | undefined>(
    undefined,
  );

  protected readonly logoData = computed(
    () =>
      (this.logoSlot()?.components?.component?.[0] as CmsSiteLogoComponent) ||
      undefined,
  );

  protected readonly navData = computed(() => {
    const slot = this.navigationBarSlot();
    const headerData = slot?.components?.component?.[0];
    return {
      ...headerData,
      navigationNode: this.#resolveCmsLinksInNavigationNode(
        headerData?.navigationNode,
      ),
    };
  });

  protected readonly breadcrumbData = toSignal(
    this.#breadcrumbsService.getBreadCrumbData(),
  );

  protected readonly itemsInCart = toSignal(
    this.#cartFacade.getActiveCart().pipe(
      filter(Boolean),
      map((cart: Cart) => cart.totalMainUnitCount),
      startWith(0),
    ),
  );

  protected readonly userData = toSignal(
    this.#userDataFacade.userData().pipe(
      map((user) => ({
        data: user,
        loggedIn: user.type === UserEnum.B2C_AUTHENTICATED,
      })),
    ),
  );

  protected readonly activeCulture = toSignal(
    this.#cultureFacade.getActiveCulture(),
  );

  //#endregion
  constructor() {
    this.#initializeSignals();
    this.#setupRouterEvents();
  }

  toggleMobileNavigationModule(value: boolean): void {
    if (value)
      this.#renderer.addClass(this.#windowRef.document.body, 'modal-open');
    else
      this.#renderer.removeClass(this.#windowRef.document.body, 'modal-open');
    this.isMobileModuleActive.set(value);
  }

  loginClicked(): void {
    this.#userDataFacade.requestLogin();
  }

  logoutClicked(): void {
    this.#userDataFacade.requestLogout();
  }

  openLanguageSelector(): void {
    this.#modalService.open(LanguageSelectorModalComponent, {
      panelClass: 'modal-language-selector',
    });
  }

  #initializeSignals(): void {
    this.#cmsFacade
      .getHeaderData()
      .pipe(
        filter(Boolean),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((data) => {
        this.siteContextSlot.set(this.#getSlotData(data, 'SiteContextSlot'));
        this.logoSlot.set(this.#getSlotData(data, 'SiteLogoSlot'));
        this.miniCartSlot.set(this.#getSlotData(data, 'MiniCartSlot'));
        this.loginSlot.set(this.#getSlotData(data, 'SiteLoginSlot'));
        this.navigationBarSlot.set(
          this.#getSlotData(data, 'NavigationBarSlot'),
        );
        this.placeholderSlot.set(
          this.#getSlotData(data, 'PlaceholderContentSlot'),
        );
        this.bottomHeaderSlot.set(this.#getSlotData(data, 'BottomHeaderSlot'));
        this.siteLinksSlot.set(this.#getSlotData(data, 'SiteLinksSlot'));
        this.searchBoxSlot.set(this.#getSlotData(data, 'SearchBoxSlot'));
      });
  }

  #setupRouterEvents(): void {
    this.#router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((event) => {
        const pathArray = event.url.slice(6).split('/').filter(Boolean);
        const useWhiteHeader =
          (pathArray.length < 3 && pathArray[0] === 'branches') ||
          (pathArray[0] === 'branches' && pathArray[1].includes('find'));
        this.useWhiteHeader.set(useWhiteHeader);
      });
  }

  #getSlotData(
    cmsComponentData: CmsHeaderComponent[],
    slotName: string,
  ): CmsHeaderComponent | undefined {
    return cmsComponentData.find(
      (a: CmsHeaderComponent) => a.slotId === slotName,
    );
  }

  #resolveCmsLinksInNavigationNode(
    node: CmsNavigationNode | undefined,
  ): CmsNavigationNode | undefined {
    if (!node) {
      return undefined;
    }

    if (node.children?.length) {
      return {
        ...node,
        children: node.children.map(
          (childNode) =>
            this.#resolveCmsLinksInNavigationNode(childNode) ?? childNode,
        ),
      };
    }

    if (node.entries?.length) {
      const resolvedData = this.#getLinkAndCategory(
        node.entries[node.entries.length - 1].itemId as string,
      );
      return { ...node, resolvedData };
    }

    return node;
  }

  #getLinkAndCategory(componentId: string): Observable<{
    category?: BoelsCategoryHierarchy;
    component?: BoelsCmsLinkComponent;
  }> {
    const activeCulture = this.activeCulture();
    if (!activeCulture) return EMPTY;

    return this.#componentDataService.getComponents(componentId).pipe(
      combineLatestWith(
        this.#siteContextFacade.getActiveBaseSiteEntity().pipe(filter(Boolean)),
      ),
      switchMap(([component, baseSite]) => {
        const tempComponent = component.component[0] as BoelsCmsLinkComponent;
        if (baseSite && baseSite.uid && tempComponent?.category) {
          const baseSiteUid = baseSite.uid as string;
          const tempComponentCategory = tempComponent.category as string;
          return this.#catalogsFacade
            .getCategoryForId(baseSiteUid, activeCulture, tempComponentCategory)
            .pipe(
              filter(Boolean),
              map((categoryHierarchy: BoelsCategoryHierarchy) => {
                const subcategories = categoryHierarchy.subcategories?.map(
                  (subCategory) => this.#addUrlToCategory(subCategory),
                );
                const category = {
                  ...this.#addUrlToCategory(categoryHierarchy),
                  subcategories,
                };
                return {
                  category,
                  component: tempComponent,
                };
              }),
            );
        }
        return of({ component: tempComponent });
      }),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  #addUrlToCategory(category: BoelsCategoryHierarchy): BoelsCategoryHierarchy {
    let url = '/';
    for (const breadcrumb of category.boelsBreadcrumbs || []) {
      url += this.#urlSafePipe.transform(breadcrumb.categoryName) + '/';
    }
    url += this.#urlSafePipe.transform(category.name) + '/c/' + category.id;
    return { ...category, url };
  }
}
